import { cn } from '@/shared/lib/cn';
import {
  AlertCircle,
  Camera,
  Check,
  CheckCheck,
  CircleDollarSign,
  Clock,
  Copy,
  CornerUpLeft,
  ExternalLink,
  Eye,
  FilePen,
  Forward,
  MapPin,
  Merge,
  Pencil,
  User,
  Vibrate,
  WandSparkles,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { dentroDoPrazoDeEdicao } from '../edicao-de-mensagem';
import { timeLabel } from '../lib/format';
import { larguraDaMidia, mediaBoxSize } from '../lib/media-box';
import { renderWhatsAppText } from '../lib/whatsapp-format';
import { agoraDoServidor } from '../relogio-do-servidor';
import type { ChatMessage } from '../types';
import { ContagemDoPacote } from './ContagemDoPacote';
import { MediaContent } from './MediaContent';
import { TemplatePreview } from './TemplatePreview';

interface Props {
  message: ChatMessage;
  onReply?: (m: ChatMessage) => void;
  /** Habilita o botão "Excluir" (revoke) na toolbar. Só faz sentido em
   *  outbound + canal qr_code dentro da janela de revoke do WhatsApp. */
  onDelete?: (m: ChatMessage) => void;
  /** Habilita "Editar" na toolbar. O pai só passa quando a msg é editável
   *  (texto, canal QR, autor/admin); o PRAZO de 15min é checado aqui, no
   *  hover — muda com o tempo sem o pai re-renderizar. */
  onEdit?: (m: ChatMessage) => void;
  /** Pulado p/ a msg citada (clicou no QuotedPreview). ThreadPanel resolve
   *  via data-wamid no DOM + scrollIntoView + highlight temporário. */
  onJumpToQuoted?: (wamid: string) => void;
  /** Fecha o pacote de texto na hora (clique na contagem). Ausente = a
   *  contagem fica só informativa. */
  onEnviarAgora?: (m: ChatMessage) => void;
  /** Descarta o pacote de texto que ainda não saiu (o texto volta pro campo
   *  de digitar). Ausente = sem a opção no menu do balão. */
  onDescartar?: (m: ChatMessage) => void;
  /** Busca na conversa: termo a destacar em amarelo no texto. */
  termoDaBusca?: string;
  /** Busca na conversa: esta é a mensagem do resultado atual (anel em volta). */
  emFoco?: boolean;
}

/**
 * Anel do resultado atual da busca. Vai no PRÓPRIO balão, não no envelope da
 * bolha: o anel (box-shadow) segue o formato de quem o recebe — cantos de 7px e
 * o canto reto do bico. No envelope ele saía com outro arredondamento e uma
 * folga, desencaixado do balão.
 */
const ANEL_DO_FOCO = 'ring-2 ring-brand-400';

const MEDIA_TYPES = new Set(['image', 'audio', 'video', 'document', 'sticker']);

interface LocationPayload {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

interface ContactItem {
  name?: string;
  /** Legado: rows antigas do worker-zapo gravaram a chave errada. */
  display_name?: string;
  phones?: string[];
  vcard?: string;
}

interface ContactsPayload {
  contacts: ContactItem[];
}

function tryParseJSON<T>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Bolha de NOTA INTERNA — observação que fica só no painel (nunca vai pro
 * cliente). Amarela e centralizada pra destacar do fluxo de mensagens do
 * WhatsApp. Mostra autor + horário. Mesma paleta de callout do sistema
 * (warning-500 border / warning-50 bg / warning-700 texto).
 */
function InternalNoteBubble({
  message,
  termoDaBusca,
  emFoco,
}: {
  message: ChatMessage;
  termoDaBusca?: string | undefined;
  emFoco?: boolean | undefined;
}) {
  const author = message.sent_by_user_name?.trim();
  return (
    <div data-message-id={message.id} className="flex w-full justify-center px-2 py-0.5">
      <div
        className={cn(
          'w-full max-w-[85%] rounded-[10px] border border-warning-500 bg-warning-50 px-3 py-2 shadow-xs',
          emFoco && ANEL_DO_FOCO,
        )}
      >
        <div className="mb-1 flex items-center gap-1.5 font-semibold text-[11px] text-warning-700">
          <FilePen size={13} strokeWidth={2} aria-hidden="true" />
          <span>Nota interna</span>
          {author && <span className="font-normal text-warning-600">· {author}</span>}
          <span className="ml-auto font-normal text-[10px] text-warning-600">
            {timeLabel(message.created_at)}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-[13.5px] text-warning-700 leading-[1.4]">
          {renderWhatsAppText(message.body ?? '', termoDaBusca)}
        </div>
      </div>
    </div>
  );
}

export function MessageBubble({
  message,
  onReply,
  onDelete,
  onEdit,
  onJumpToQuoted,
  onEnviarAgora,
  onDescartar,
  termoDaBusca,
  emFoco,
}: Props) {
  // Nota interna: renderização própria (amarela), distinta de mensagem WhatsApp.
  // Fica só no painel — nunca foi/vai pro cliente. Curto-circuito antes de toda
  // a lógica de bolha (toolbar, checkmarks, mídia, quote) que não se aplica.
  if (message.internal_note) {
    return <InternalNoteBubble message={message} termoDaBusca={termoDaBusca} emFoco={emFoco} />;
  }

  const isMe = message.direction === 'outbound';
  // Selo "paga" (Opção 3): saída pela API oficial da Meta (cobrada). Acento
  // âmbar na lateral + "Cobrada" no rodapé. Não vale pra figurinha (bolha solta).
  const isBillable = isMe && message.billable === true && message.type !== 'sticker';
  // Selo de agrupamento: 2+ textos do atendente sairam nesta mensagem so.
  // Mesmo canto do "Cobrada" (esquerda do rodape).
  const isBatched = isMe && message.batched === true;
  // Saiu pelo CELULAR do lojista (coexistência) e não pelo painel. Mesmo canto
  // dos outros selos — deixa visível na conversa quem respondeu de onde.
  const isFromPhone = isMe && message.from_phone === true;
  // A IA reescreveu o pacote: o texto acima é o dela, não o que foi digitado.
  // O hover mostra o original — o atendente precisa poder conferir o que saiu
  // no lugar do que ele escreveu, sem sair da conversa.
  const isAiRewritten = isMe && message.ai_rewritten === true;
  // Pacote de texto ainda aberto: o texto nao saiu, esta esperando os proximos
  // segundos pra ir tudo junto. Vira null assim que o prazo passa.
  const pacoteAberto =
    isMe &&
    message.batch_open_until &&
    new Date(message.batch_open_until).getTime() > agoraDoServidor()
      ? message.batch_open_until
      : null;
  // Renderiza MediaContent assim que sabemos que é mídia, mesmo antes de
  // media_id existir — whatsmeow faz download async, MediaContent mostra
  // skeleton enquanto pending pra evitar flash de [audio]/[image].
  const hasMedia =
    MEDIA_TYPES.has(message.type) && (message.media_id || message.media_status === 'pending');

  // Prende a largura da bolha à da mídia quando há imagem/vídeo (com legenda) ou
  // template com header de mídia — senão a legenda/corpo estica até max-w-480 e
  // fica mais larga que a imagem (desalinhado). +24px = px-3 (12px de cada lado).
  const constrainToMedia =
    (Boolean(hasMedia) && (message.type === 'image' || message.type === 'video')) ||
    (message.type === 'template' && Boolean(message.template_preview?.headerMedia));
  // +24px = px-3 (12px de cada lado). A largura vem da função do TIPO da mídia:
  // vídeo e imagem têm caixas diferentes, e medir a bolha pela da imagem fazia
  // a prévia do vídeo vazar pra fora dela.
  const bubbleMaxWidth = constrainToMedia
    ? larguraDaMidia(message.type, message.media_width, message.media_height) + 24
    : undefined;

  // Avisos do sistema (msgs sintéticas inbound criadas pelo worker quando algo
  // do cliente não pôde ser processado — ex: ZIP >16MiB rejeitado pelo MinIO).
  // Detecta pelo prefixo ⚠️ — mais leve que criar novo enum de tipo.
  const isSystemAlert =
    !isMe &&
    message.type === 'text' &&
    typeof message.body === 'string' &&
    message.body.startsWith('⚠️ ');

  // Toolbar fica visível só com hover. Delay no hide (150ms) pra forgive
  // movimento do mouse atravessando o pequeno gap visual entre bubble e
  // toolbar — sem isso, sair do bubble UP esconde a toolbar antes do user
  // chegar nela.
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onEnter = (): void => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHovered(true);
  };
  const onLeave = (): void => {
    // Cancela um "sumir" anterior antes de marcar outro: com dois timers
    // vivos, voltar o mouse cancelava só o último e o outro escondia o menu
    // com o mouse em cima do balão (bug relatado pelo dono, 18/out/2026).
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
      leaveTimerRef.current = null;
    }, 150);
  };
  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  // Texto copiável. Pra localização o body é JSON cru — copia algo legível
  // (nome + endereço + link do mapa), senão o atendente acaba colando o JSON
  // no composer e mandando isso pro cliente. Demais tipos: o body.
  const copyableText: string | undefined = (() => {
    if (message.type === 'location') {
      const loc = tryParseJSON<LocationPayload>(message.body);
      if (loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
        const maps = `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`;
        return [loc.name, loc.address, maps].filter(Boolean).join('\n').trim();
      }
    }
    return message.body?.trim() || undefined;
  })();

  const copyText = (): void => {
    if (!copyableText) {
      toast.error('Mensagem sem texto pra copiar');
      return;
    }
    navigator.clipboard
      .writeText(copyableText)
      .then(() => toast.success('Copiado'))
      .catch(() => toast.error('Falha ao copiar'));
  };

  const isDeleted = Boolean(message.deleted_at);
  const isEdited = Boolean(message.edited_at);
  // Copiar continua disponível mesmo em msgs apagadas — o body é preservado
  // localmente, agente ainda pode querer extrair o conteúdo.
  const canCopy = Boolean(copyableText);
  // Reply NÃO funciona em deleted (WhatsApp recusa quote de msg revogada).
  const canReply = onReply !== undefined && Boolean(message.wamid) && !isDeleted;
  // Excluir: só em outbound, com wamid, e quando o pai (ThreadPanel) decide
  // habilitar o callback (passa onDelete só se canal é qr_code e dentro da
  // janela de revoke). Já apagada não pode ser apagada de novo.
  const canDelete = onDelete !== undefined && Boolean(message.wamid) && !isDeleted;
  // Editar: o pai já filtrou tipo/canal/autor; o prazo é lido na hora (o
  // hover re-renderiza, então o botão some sozinho depois dos 15min).
  const canEdit =
    hovered &&
    onEdit !== undefined &&
    Boolean(message.wamid) &&
    !isDeleted &&
    dentroDoPrazoDeEdicao(message.sent_at);
  // Descartar: só enquanto o pacote do agrupador ainda espera pra sair (depois
  // de enviada, o caminho é o Excluir). Fica no menu do hover, não no balão —
  // decisão do dono (17/out/2026).
  const canDiscard =
    onDescartar !== undefined && pacoteAberto !== null && message.id > 0 && !isDeleted;
  const showToolbar = hovered && (canCopy || canReply || canEdit || canDelete || canDiscard);

  return (
    <div
      // data-wamid: usado pelo ThreadPanel pra encontrar o bubble e fazer
      // scrollIntoView quando clicam num QuotedPreview que aponta pra ele.
      // Só populado em msgs com wamid (sem isso o querySelector não acha).
      {...(message.wamid ? { 'data-wamid': message.wamid } : {})}
      // data-message-id: a busca na conversa acha a bolha por ele (nem toda
      // mensagem tem wamid — a que falhou no envio, por exemplo).
      data-message-id={message.id}
      className={cn(
        'group relative flex flex-col transition-colors duration-500',
        isMe ? 'self-end' : 'self-start',
      )}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      // Rede de segurança: se o menu sumiu com o mouse ainda em cima (qualquer
      // corrida de eventos), mexer o mouse no balão traz de volta. Só chama
      // quando está escondido — sem custo enquanto o menu está aberto.
      onMouseMove={hovered ? undefined : onEnter}
    >
      {/* Agrupador: o texto ainda está esperando pra sair junto com os
          próximos. Anel com os segundos, ao lado do balão — sinaliza que a
          mensagem não travou, tem hora pra sair. No hover vira o atalho de
          enviar agora, pra quando o cliente está esperando na linha.

          O 38 do deslocamento = os 32px do anel + 6 de respiro até o balão.
          Mexer no tamanho do anel sem mexer aqui faz um encostar no outro. */}
      {pacoteAberto && (
        <div className="-left-[38px] -translate-y-1/2 absolute top-1/2">
          <ContagemDoPacote
            until={pacoteAberto}
            heldMs={message.batch_held_remaining_ms ?? null}
            onEnviarAgora={onEnviarAgora ? () => onEnviarAgora(message) : undefined}
          />
        </div>
      )}
      {showToolbar && (
        <div
          // Toolbar compacta sobreposta ao topo do bubble. `bottom-full` âncora
          // base no topo, `-mb-2` puxa 8px PRA BAIXO (invade o bubble) —
          // cria visual de "flutuando sobre" tipo WhatsApp Web. Como tá dentro
          // do wrapper, mouse atravessar o overlap mantém hover.
          // SEM onMouseEnter/Leave próprios: o menu é filho do wrapper, então
          // ir do balão pro menu nem conta como "sair". Com os handlers aqui,
          // voltar do menu pro balão marcava o sumiço e o wrapper (que nunca
          // foi deixado) não cancelava — o menu sumia com o mouse no balão.
          // `menu-do-balao-entra`: desce como cortina ao aparecer (globals.css).
          className={cn(
            'menu-do-balao-entra absolute bottom-full z-10 -mb-2 flex items-center gap-0.5 rounded-md border border-n-200 bg-n-0 px-0.5 py-0.5 shadow-sm',
            'right-1.5',
          )}
        >
          {canCopy && <ToolbarButton icon={Copy} label="Copiar Texto" onClick={copyText} />}
          {canReply && (
            <ToolbarButton
              icon={CornerUpLeft}
              label="Responder"
              onClick={() => onReply?.(message)}
            />
          )}
          {canEdit && (
            <ToolbarButton icon={Pencil} label="Editar" onClick={() => onEdit?.(message)} />
          )}
          {canDelete && (
            <ToolbarButton icon={X} label="Excluir" danger onClick={() => onDelete?.(message)} />
          )}
          {canDiscard && (
            // Mesmo X vermelho do Excluir (dono, 17/out/2026); texto curto pra
            // dica não cortar na borda da conversa.
            <ToolbarButton
              icon={X}
              label="Descartar"
              danger
              onClick={() => onDescartar?.(message)}
            />
          )}
        </div>
      )}

      <div
        className={cn(
          // Estilo "WhatsApp Web": cantos pequenos (7px), padding compacto.
          // O canto reto do lado do remetente (canto inferior direito pra outbound,
          // inferior esquerdo pra inbound) cria o "bico" sutil que indica origem.
          // max-w absoluto em vez de 80% relativo — em telas largas, textos
          // enormes quebravam em 1 única linha kilométrica.
          'flex min-w-[80px] max-w-[480px] flex-col gap-1.5 break-words text-[13px] leading-[1.35]',
          // Sticker: bolha sem bg/border/padding (estilo WhatsApp Web — figurinha
          // fica "solta"). Demais tipos: bolha normal com padding 12×10.
          message.type === 'sticker' ? 'p-0' : 'px-3 py-2.5',
          message.type === 'sticker'
            ? 'bg-transparent'
            : isSystemAlert
              ? 'rounded-[7px_7px_7px_0] border border-danger-300 bg-danger-50 font-medium text-danger-700'
              : isMe
                ? 'force-light rounded-[7px_7px_0_7px] border border-[#86efb8] bg-[#c3f7d8] text-[#09090B]'
                : 'rounded-[7px_7px_7px_0] border border-[var(--bubble-recv-border)] bg-[var(--bubble-recv-bg)] text-[var(--bubble-recv-text)]',
          emFoco && ANEL_DO_FOCO,
        )}
        {...(bubbleMaxWidth ? { style: { maxWidth: `${bubbleMaxWidth}px` } } : {})}
      >
        {isDeleted && (
          <span className="self-start rounded-pill bg-danger-50 px-2 py-0.5 font-semibold text-[10px] text-danger-600">
            Excluída
          </span>
        )}
        {isEdited && !isDeleted && (
          <span className="self-start rounded-pill bg-warning-50 px-2 py-0.5 font-semibold text-[10px] text-warning-700">
            Editada
          </span>
        )}
        {message.forwarded && (
          // Igual ao WhatsApp: rótulo discreto no topo, vale pra qualquer tipo
          // (texto, áudio, foto, documento, figurinha).
          <span className="flex items-center gap-1 self-start text-[11px] text-n-500 italic">
            <Forward size={12} strokeWidth={1.75} className="flex-shrink-0" />
            {message.frequently_forwarded ? 'Encaminhada com frequência' : 'Encaminhada'}
          </span>
        )}
        {message.reply_to && (
          <QuotedPreview
            quote={message.reply_to}
            isMe={isMe}
            {...(onJumpToQuoted && { onClick: () => onJumpToQuoted(message.reply_to!.wamid) })}
          />
        )}
        {message.status_reply && <StatusReplyBadge statusReply={message.status_reply} />}
        {hasMedia && <MediaContent message={message} />}
        {message.type === 'template' && message.template_preview ? (
          // Template HSM (campanha): renderiza header/body/footer/botões igual o
          // cliente vê. media_url cobre o header de mídia (sem precisar de ticket).
          <TemplatePreview message={message} preview={message.template_preview} />
        ) : message.type === 'location' ? (
          <LocationContent body={message.body} />
        ) : message.type === 'contacts' ? (
          <ContactsContent body={message.body} />
        ) : message.type === 'unsupported' ? (
          // type=unsupported (Cloud API): conteúdo que a Cloud não consegue
          // representar/transmitir. Vale pra INBOUND (cliente mandou algo que a
          // Cloud não passou — bug comum) E OUTBOUND (echo de coexistência do
          // celular do dono). Placeholder amigável no lugar de "[unsupported]".
          <span className="whitespace-pre-wrap">
            Uma mensagem <strong>não suportada pela Cloud API</strong> foi recebida.
          </span>
        ) : message.body ? (
          // whitespace-pre-wrap preserva espaços múltiplos; renderWhatsAppText cuida
          // de \n via <br/> e dos marcadores *negrito*, _italico_, ~riscado~, `mono`.
          <span className="whitespace-pre-wrap">
            {renderWhatsAppText(message.body, termoDaBusca)}
          </span>
        ) : !hasMedia ? (
          <span className="opacity-60">[{message.type}]</span>
        ) : null}
        <div
          className={cn(
            // gap-0.5 do pai já dá 2px; sem mt negativo aqui pra preservar respiro
            // entre texto e timer (line-height do texto deixava colado demais).
            'flex items-center gap-1 text-[9px]',
            isMe ? 'justify-end text-[#71717A]' : 'justify-start text-[var(--bubble-recv-meta)]',
          )}
        >
          {(isBillable || isBatched || isFromPhone || isAiRewritten) && (
            // Selos do canto esquerdo do rodapé — só ícone, com o significado
            // no tooltip. Quando mais de um se aplica, ficam lado a lado em vez
            // de brigar pelo `mr-auto`.
            //
            // Ordem fixa: POR ONDE saiu primeiro (cobrada OU celular — são
            // mutuamente exclusivos: o que sai pelo aparelho não passa pela
            // Cloud), depois o que aconteceu com o texto (agrupado, melhorado).
            // Assim a primeira posição responde sempre a mesma pergunta.
            <span className="mr-auto inline-flex items-center gap-1.5">
              {isBillable && (
                <span
                  className="inline-flex items-center text-[#b45309]"
                  title="Mensagem cobrada"
                  aria-label="Mensagem cobrada"
                >
                  <CircleDollarSign size={11} strokeWidth={2} />
                </span>
              )}
              {isFromPhone && (
                <span
                  className="inline-flex items-center text-[#0f766e]"
                  title="Enviada pelo celular"
                  aria-label="Enviada pelo celular"
                >
                  <Vibrate size={11} strokeWidth={2} />
                </span>
              )}
              {isBatched && (
                <span
                  className="inline-flex items-center text-[#2563eb]"
                  title="Mensagens agrupadas"
                  aria-label="Mensagens agrupadas"
                >
                  <Merge size={11} strokeWidth={2} />
                </span>
              )}
              {isAiRewritten &&
                (message.ai_original ? (
                  // Card de hover em vez do `title` do HTML: o atributo nativo
                  // não honra `\n` de forma confiável e o texto original vinha
                  // esparramado numa linha só. Mesmo componente do olhinho de
                  // mensagem editada (logo abaixo) — os dois mostram "o que
                  // estava escrito antes", então mostram igual.
                  <span className="group/ia relative inline-flex items-center">
                    <WandSparkles
                      size={11}
                      strokeWidth={2}
                      className="cursor-help text-[#7c3aed]"
                      aria-label="Melhorada por IA"
                    />
                    <span
                      role="tooltip"
                      className={cn(
                        'pointer-events-none absolute bottom-full z-20 mb-1.5 w-max max-w-[260px] rounded-md border border-n-200 bg-n-0 px-2.5 py-1.5 text-left text-n-800 text-xs opacity-0 shadow-md transition group-hover/ia:opacity-100',
                        isMe ? 'right-0' : 'left-0',
                      )}
                    >
                      <span className="mb-0.5 block font-semibold text-[10px] text-n-500 uppercase tracking-wide">
                        Mensagem original:
                      </span>
                      <span className="block whitespace-pre-wrap break-words">
                        {message.ai_original}
                      </span>
                    </span>
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center text-[#7c3aed]"
                    title="Melhorada por IA"
                    aria-label="Melhorada por IA"
                  >
                    <WandSparkles size={11} strokeWidth={2} />
                  </span>
                ))}
            </span>
          )}
          {isEdited && message.original_body && (
            // Olhinho com popover de hover: revela o `original_body` (versão
            // antes da 1ª edição) num card flutuante quando o cursor entra no
            // ícone. Não-interativo — apenas leitura visual.
            <div className="group/eye relative inline-flex items-center">
              <Eye
                size={11}
                strokeWidth={1.75}
                aria-label="Ver mensagem original"
                className="cursor-help"
              />
              <div
                role="tooltip"
                className={cn(
                  'pointer-events-none absolute bottom-full z-20 mb-1.5 w-max max-w-[260px] rounded-md border border-n-200 bg-n-0 px-2.5 py-1.5 text-left text-n-800 text-xs opacity-0 shadow-md transition group-hover/eye:opacity-100',
                  // Inbound (esquerda): popover sai pra direita do olho.
                  // Outbound (direita): popover sai pra esquerda. Evita
                  // overflow no painel de inbox.
                  isMe ? 'right-0' : 'left-0',
                )}
              >
                <div className="mb-0.5 font-semibold text-[10px] text-n-500 uppercase tracking-wide">
                  Mensagem original
                </div>
                <div className="whitespace-pre-wrap break-words">{message.original_body}</div>
              </div>
            </div>
          )}
          {timeLabel(message.created_at)}
          {isMe && <StatusIndicator m={message} />}
        </div>
        {message.reaction && (
          // Pill da reação do cliente — colada no canto inferior do bubble,
          // levemente sobreposta. Outbound: canto direito; inbound: esquerdo
          // (espelha lado do bubble). Padrão WhatsApp Web.
          <span
            className={cn(
              '-bottom-2 absolute inline-flex h-5 min-w-5 select-none items-center justify-center rounded-pill border border-n-200 bg-n-0 px-1 text-[11px] shadow-sm',
              isMe ? 'right-2' : 'left-2',
            )}
            title="Reação do cliente"
            aria-label={`Reação: ${message.reaction}`}
          >
            {message.reaction}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Botão da toolbar de ação (copy/reply) com tooltip custom.
 * Tooltip aparece acima do botão ao hover — dark bg + texto branco, padrão
 * WhatsApp Web. Usa `group/btn` (Tailwind nested groups) pra escopar o
 * group-hover ao próprio botão sem pegar hover do wrapper externo.
 */
function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof Copy;
  label: string;
  onClick: () => void;
  /** Estiliza em vermelho — usado pra ações destrutivas (Excluir). */
  danger?: boolean;
}) {
  // Dica pelo `title`: quem desenha é a camada global (TooltipLayer) — mesma
  // espera de 200ms, fade e visual dos outros tooltips, e `position: fixed`
  // (não corta na borda da conversa, como cortava o span próprio de antes).
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        title={label}
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded transition',
          danger
            ? 'text-danger-500 hover:bg-danger-50 hover:text-danger-700'
            : 'text-n-500 hover:bg-n-100 hover:text-n-700',
        )}
      >
        <Icon size={12} strokeWidth={1.75} />
      </button>
    </div>
  );
}

/**
 * Preview da msg quotada renderizado dentro do bubble (acima do corpo da msg
 * de resposta). Estilo similar ao banner do Composer: barra colorida vertical
 * + label + 1 linha truncada.
 */
function QuotedPreview({
  quote,
  isMe,
  onClick,
}: {
  quote: NonNullable<ChatMessage['reply_to']>;
  isMe: boolean;
  /** Quando setado, clicar no preview faz jump pra msg citada. */
  onClick?: () => void;
}) {
  const previewText = (() => {
    if (quote.body && quote.body.trim()) return quote.body.trim();
    if (quote.type === 'image') return '[Imagem]';
    if (quote.type === 'video') return '[Vídeo]';
    if (quote.type === 'audio') return '[Áudio]';
    if (quote.type === 'document') return '[Documento]';
    if (quote.type === 'sticker') return '[Sticker]';
    if (quote.type === 'location') return '[Localização]';
    if (quote.type === 'contacts') return '[Contato]';
    return `[${quote.type}]`;
  })();

  // Cor do fundo + barra adapta ao bubble: outbound (verde) usa tom mais
  // escuro pra contraste; inbound (cinza) usa tom mais claro.
  const bg = isMe ? 'bg-[#a8edc4]' : 'bg-[var(--bubble-recv-accent)]';

  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex items-stretch gap-2 rounded-md p-1.5 text-left',
        bg,
        onClick && 'cursor-pointer transition-opacity hover:opacity-80',
      )}
    >
      <div className="w-1 flex-shrink-0 rounded-pill bg-brand-500" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
        <span className="font-semibold text-[11px] text-brand-600">
          {quote.direction === 'outbound' ? 'Você' : 'Cliente'}
        </span>
        <span className="truncate text-[12px] text-n-700">{previewText}</span>
      </div>
    </Tag>
  );
}

function LocationContent({ body }: { body: string | null }) {
  const loc = tryParseJSON<LocationPayload>(body);
  if (!loc || typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
    return <span className="opacity-60">[localização]</span>;
  }
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`;
  return (
    <a
      href={mapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="-m-1 flex items-start gap-2 rounded-md p-1 transition hover:bg-black/5"
    >
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-n-0 text-brand-600">
        <MapPin size={18} strokeWidth={2} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {loc.name && <span className="truncate font-semibold text-[13px]">{loc.name}</span>}
        {loc.address && <span className="truncate text-[12px] text-n-700">{loc.address}</span>}
        {!loc.name && !loc.address && (
          <span className="font-mono text-[11px] text-n-700">
            {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
          </span>
        )}
        <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-brand-600">
          Abrir no Google Maps
          <ExternalLink size={11} strokeWidth={2} />
        </span>
      </span>
    </a>
  );
}

function ContactsContent({ body }: { body: string | null }) {
  const data = tryParseJSON<ContactsPayload>(body);
  if (!data || !Array.isArray(data.contacts) || data.contacts.length === 0) {
    return <span className="opacity-60">[contato]</span>;
  }
  return (
    <div className="flex flex-col gap-1.5">
      {data.contacts.map((c, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-pill bg-n-0 text-n-500">
            <User size={18} strokeWidth={1.75} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate font-semibold text-[13px]">
              {c.name || c.display_name || 'Contato'}
            </span>
            {(c.phones ?? []).map((p, j) => (
              <span key={j} className="truncate font-mono text-[11px] text-n-700">
                {p}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusIndicator({ m }: { m: ChatMessage }) {
  if (m.failed_at) {
    // Tooltip mostra "#{code} – {title}" quando temos ambos; senão fallback.
    // Code identifica origem (Meta ex: 131042, 132000; ou interno ex:
    // SYSTEM_DEGRADED, PUBLISH_FAILED) — atendente consegue acionar suporte.
    const parts: string[] = [];
    if (m.error_code) parts.push(`#${m.error_code}`);
    if (m.error_title) parts.push(m.error_title);
    const tooltip = parts.length > 0 ? parts.join(' – ') : 'Falha no envio';
    // Só o ícone — o código/título aparece no tooltip ao passar o mouse.
    return (
      <span title={tooltip}>
        <AlertCircle size={13} className="text-danger-500" />
      </span>
    );
  }
  // Prefere timestamps quando setados (msgs enviadas pelo ChatPlus recebem
  // callbacks de status do WhatsApp). Como fallback, usa `status_rank` — cobre
  // mensagens espelhadas de outro device linkado (canal QR, source='other_device'),
  // onde o worker grava rank=2 mas pode não ter callback de status explícito.
  if (m.read_at || (m.status_rank ?? 0) >= 3) {
    return <CheckCheck size={13} className="text-brand-500" />;
  }
  if (m.delivered_at || (m.status_rank ?? 0) >= 2) return <CheckCheck size={13} />;
  if (m.sent_at || (m.status_rank ?? 0) >= 1) return <Check size={13} />;
  // Pending (status_rank=0, sem timestamps) — typicamente bolha optimistic
  // recém-inserida pelo onMutate, aguardando confirmação de envio. Reloginho
  // 🕒 sinaliza "tá processando" no padrão WhatsApp/Telegram.
  return <Clock size={11} className="opacity-60" />;
}

/**
 * Badge que aparece acima da bolha quando a msg é resposta a um status
 * do dono do canal. Backend detecta via ContextInfo.statusSourceType
 * (worker whatsmeow). Layout (paridade com o mobile): card em LINHA —
 * thumbnail à ESQUERDA, label "Em resposta ao seu status" + caption à
 * direita. Status text-only cai em placeholder com ícone de câmera.
 */
function StatusReplyBadge({
  statusReply,
}: {
  statusReply: NonNullable<ChatMessage['status_reply']>;
}) {
  const thumbB64 = statusReply.status_thumbnail_b64;
  const caption = statusReply.status_caption?.trim();
  return (
    <div className="flex max-w-[260px] items-center gap-2 self-start rounded-md bg-black/5 p-1.5">
      {thumbB64 ? (
        <img
          src={`data:image/jpeg;base64,${thumbB64}`}
          alt="Status"
          className="h-[44px] w-[44px] flex-shrink-0 rounded-md object-cover"
        />
      ) : (
        <div className="flex h-[44px] w-[44px] flex-shrink-0 items-center justify-center rounded-md bg-n-200 text-n-500">
          <Camera size={18} />
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-1 text-[11px] font-semibold text-n-700">
          <Camera size={11} className="flex-shrink-0 opacity-70" />
          {/* Instagram: resposta a um story da conta (mesmo campo, tipo próprio). */}
          {statusReply.status_type === 'ig_story'
            ? 'Respondeu ao seu story'
            : 'Em resposta ao seu status'}
        </div>
        {caption ? <span className="truncate text-[12px] text-n-600">{caption}</span> : null}
      </div>
    </div>
  );
}
